// Transformers.js is loaded via CDN in index.html and available as window.transformers
const waitForTransformers = () => {
    return new Promise((resolve, reject) => {
        if (window.transformers) {
            resolve(window.transformers);
            return;
        }

        // Wait for transformers to load (max 30 seconds)
        const timeout = setTimeout(() => {
            reject(new Error('Transformers.js failed to load from CDN within 30 seconds'));
        }, 30000);

        const checkInterval = setInterval(() => {
            if (window.transformers) {
                clearInterval(checkInterval);
                clearTimeout(timeout);
                resolve(window.transformers);
            }
        }, 100);
    });
};

/**
 * Extract JSON objects from text and find the one that looks like an AI response.
 * Handles markdown code blocks and uses balanced brace matching for nested objects.
 */
function extractFirstJson(text) {
    if (!text) return null;

    // Remove markdown code blocks if present
    let cleanText = text;
    // Try to extract from complete code block first
    let codeBlockMatch = text.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/);
    if (!codeBlockMatch) {
        // Try incomplete code block (missing closing ```)
        codeBlockMatch = text.match(/```(?:json)?\s*(\{[\s\S]*)/);
    }
    if (codeBlockMatch) {
        cleanText = codeBlockMatch[1];
        console.log('[AI] Extracted JSON from markdown code block');
    }

    const jsonObjects = [];
    let searchStart = 0;
    
    while (searchStart < cleanText.length) {
        const openBrace = cleanText.indexOf('{', searchStart);
        if (openBrace === -1) break;
        
        let depth = 0;
        let inString = false;
        let escapeNext = false;
        
        for (let i = openBrace; i < cleanText.length; i++) {
            const char = cleanText[i];
            
            if (escapeNext) {
                escapeNext = false;
                continue;
            }
            
            if (char === '\\') {
                escapeNext = true;
                continue;
            }
            
            if (char === '"') {
                inString = !inString;
                continue;
            }
            
            if (!inString) {
                if (char === '{') {
                    depth++;
                } else if (char === '}') {
                    depth--;
                    if (depth === 0) {
                        const jsonStr = cleanText.substring(openBrace, i + 1);
                        try {
                            const obj = JSON.parse(jsonStr);
                            jsonObjects.push(obj);
                        } catch (e) {
                            // Invalid JSON, continue searching
                        }
                        searchStart = i + 1;
                        break;
                    }
                }
            }
        }
        
        if (depth !== 0) break;
    }
    
    // Find the JSON that looks like an AI response (has expected fields)
    for (let i = 0; i < jsonObjects.length; i++) {
        const obj = jsonObjects[i];
        
        // Check if it has AI response fields (not input data fields)
        if (obj.headline || obj.summary || obj.recommendations || obj.message || 
            obj.trends || obj.strengths || obj.concerns) {
            console.log(`[AI] Found valid AI response (object ${i} of ${jsonObjects.length})`);
            return obj;
        }
    }
    
    // Fallback 1: last complete JSON object found
    if (jsonObjects.length > 0) {
        return jsonObjects[jsonObjects.length - 1];
    }

    // Fallback 2: attempt to repair truncated JSON (e.g. generation cut off mid-response)
    const repaired = tryRepairTruncatedJson(cleanText);
    if (repaired) {
        console.log('[AI] Recovered JSON from truncated response via auto-repair');
        return repaired;
    }

    return null;
}

/**
 * Attempt to repair a truncated JSON string by auto-closing open strings,
 * arrays, and objects. Used when generation is cut off mid-response.
 */
function tryRepairTruncatedJson(text) {
    const start = text.indexOf('{');
    if (start === -1) return null;

    let slice = text.slice(start);

    // Walk the characters to track open structures
    const stack = []; // holds '{', '[', or '"'
    let escape = false;
    let inString = false;

    for (let i = 0; i < slice.length; i++) {
        const ch = slice[i];

        if (escape) { escape = false; continue; }
        if (ch === '\\') { escape = true; continue; }

        if (inString) {
            if (ch === '"') {
                inString = false;
                stack.pop(); // pop the '"' marker
            }
            continue;
        }

        if (ch === '"') { inString = true; stack.push('"'); continue; }
        if (ch === '{' || ch === '[') { stack.push(ch); continue; }
        if (ch === '}' || ch === ']') { stack.pop(); continue; }
    }

    // If nothing is open, nothing to repair (but parse failed anyway - bail)
    if (stack.length === 0) return null;

    // Build the closing sequence in reverse stack order
    let repair = '';

    // Close a trailing incomplete string first
    if (stack[stack.length - 1] === '"') {
        repair += '"';
        stack.pop();
    }

    // Remove trailing partial token (incomplete key or value without terminator)
    // Find last clean separator (',' or '{' or '[' or ':') and trim after it
    let trimmed = slice + repair;
    const lastSafeChar = Math.max(
        trimmed.lastIndexOf(','),
        trimmed.lastIndexOf('{'),
        trimmed.lastIndexOf('['),
        trimmed.lastIndexOf('"')
    );

    if (lastSafeChar > 0 && stack.length > 0) {
        // Cut back to last safe point (remove partial trailing content)
        const lastChar = trimmed[lastSafeChar];
        if (lastChar === ',') {
            // Drop trailing comma to avoid `,]` or `,}`
            trimmed = trimmed.slice(0, lastSafeChar);
        }
    }

    // Close all open containers
    while (stack.length > 0) {
        const open = stack.pop();
        if (open === '{') trimmed += '}';
        else if (open === '[') trimmed += ']';
    }

    try {
        return JSON.parse(trimmed);
    } catch (e) {
        return null;
    }
}

class AIService {
    constructor() {
        this.model = null;
        this.processor = null;
        this.isLoading = false;
        this.isReady = false;
        this.loadProgress = 0;
        this.error = null;
        this._loadPromise = null;
        this._listeners = [];
        this._instanceId = Date.now();
        console.log(`[AI] AIService instance created: ${this._instanceId}`);
    }

    subscribe(callback) {
        this._listeners.push(callback);
        return () => {
            this._listeners = this._listeners.filter(cb => cb !== callback);
        };
    }

    _notify() {
        const state = this.getState();
        this._listeners.forEach(cb => cb(state));
    }

    getState() {
        return {
            isLoading: this.isLoading,
            isReady: this.isReady,
            loadProgress: this.loadProgress,
            error: this.error
        };
    }

    async checkWebGPU() {
        if (!navigator.gpu) return { available: false, reason: 'WebGPU not supported' };
        try {
            const adapter = await navigator.gpu.requestAdapter();
            if (!adapter) return { available: false, reason: 'No WebGPU adapter' };
            return { available: true };
        } catch (e) {
            return { available: false, reason: e.message };
        }
    }

    async loadModel() {
        console.log(`[AI] loadModel called (instance: ${this._instanceId}, isReady: ${this.isReady}, isLoading: ${this.isLoading})`);
        
        if (this._loadPromise) {
            console.log(`[AI] Model already loading, returning existing promise`);
            return this._loadPromise;
        }

        if (this.isReady) {
            console.log(`[AI] Model already loaded and ready`);
            return true;
        }

        console.log(`[AI] Starting new model load...`);
        this._loadPromise = this._doLoadModel();
        return this._loadPromise;
    }

    async _doLoadModel() {
        this.isLoading = true;
        this.error = null;
        this._notify();

        try {
            // Wait for transformers.js to load from CDN
            console.log('[AI] Waiting for transformers.js CDN...');
            const transformers = await waitForTransformers();
            console.log('[AI] Transformers.js loaded, available exports:', Object.keys(transformers));
            
            const { AutoProcessor, Gemma4ForConditionalGeneration, pipeline, env } = transformers;
            
            const modelId = 'onnx-community/gemma-4-E2B-it-ONNX';
            const webgpu = await this.checkWebGPU();
            const device = webgpu.available ? 'webgpu' : 'wasm';
            
            // Configure transformers.js environment
            env.allowLocalModels = false;
            env.useBrowserCache = true;

            console.log(`[AI] Loading Gemma 4 model (${device.toUpperCase()})...`);
            console.log('[AI] Model ID:', modelId);
            console.log('[AI] This may take several minutes on first load (~2.5GB download)...');

            // Check if Gemma4 classes are available
            if (AutoProcessor && Gemma4ForConditionalGeneration) {
                console.log('[AI] Using Gemma4 API with chat template support');
                
                // Load processor first
                this.processor = await AutoProcessor.from_pretrained(modelId, {
                    progress_callback: (progress) => {
                        this._handleProgress(progress);
                    }
                });

                // Load Gemma 4 model
                this.model = await Gemma4ForConditionalGeneration.from_pretrained(modelId, {
                    dtype: 'q4f16',
                    device: device,
                    progress_callback: (progress) => {
                        this._handleProgress(progress);
                    }
                });
                
                console.log('[AI] Gemma 4 model ready with chat template support');
            } else {
                console.warn('[AI] Gemma4 classes not available, falling back to pipeline');
                
                // Fallback to pipeline
                this.model = await pipeline('text-generation', modelId, {
                    dtype: 'q4f16',
                    device: device,
                    progress_callback: (progress) => {
                        this._handleProgress(progress);
                    }
                });
                this.processor = null;
                
                console.log('[AI] Model loaded via pipeline (limited functionality)');
            }

            this.isReady = true;
            this.isLoading = false;
            this.loadProgress = 100;
            this._notify();

            return true;
        } catch (error) {
            console.error('[AI] Load failed:', error);
            this.error = error.message;
            this.isLoading = false;
            this._loadPromise = null;
            this._notify();
            throw error;
        }
    }

    _handleProgress(progress) {
        console.log('[AI] Progress:', progress);
        if (progress.progress) {
            this.loadProgress = Math.round(progress.progress);
            console.log(`[AI] Loading: ${this.loadProgress}%`);
            this._notify();
        }
        if (progress.status) {
            console.log(`[AI] Status: ${progress.status}`, progress.file || '');
        }
    }

    dispose() {
        console.log(`[AI] Disposing model (instance: ${this._instanceId})`);
        
        if (this.model && typeof this.model.dispose === 'function') {
            this.model.dispose();
        }
        
        if (this.processor && typeof this.processor.dispose === 'function') {
            this.processor.dispose();
        }
        
        this.model = null;
        this.processor = null;
        this.isReady = false;
        this.isLoading = false;
        this._loadPromise = null;
        this.loadProgress = 0;
        
        console.log(`[AI] Model disposed, memory should be freed`);
    }

    async analyze(data, promptTemplate, options = {}) {
        if (!this.isReady) {
            throw new Error('Model not loaded. Call loadModel() first.');
        }

        const { maxTokens = 512, onPartial = null } = options;
        
        // Log summary instead of full data to avoid memory bloat in DevTools
        const dataSize = JSON.stringify(data).length;
        console.log(`[AI] Analyzing data: ${data.athleteName || 'Unknown'}, ${data.weeklyAggregates?.length || 0} weeks, ${dataSize} bytes`);
        
        const prompt = typeof promptTemplate === 'function' ? promptTemplate(data) : promptTemplate;

        try {
            // Check if we're using Gemma 4 API (has processor and generate method)
            if (this.processor && this.model.generate) {
                console.log('[AI] Using Gemma 4 API with chat template');
                
                // Format prompt with chat template (KEY FIX!)
                console.log('[AI] Step 1: Creating messages...');
                const messages = [
                    { role: 'user', content: [{ type: 'text', text: prompt }] }
                ];
                
                console.log('[AI] Step 2: Applying chat template...');
                const formattedPrompt = this.processor.apply_chat_template(messages, {
                    add_generation_prompt: true,
                    enable_thinking: false
                });
                console.log('[AI] Formatted prompt length:', formattedPrompt.length);
                
                console.log('[AI] Step 3: Processing inputs...');
                const inputs = await this.processor(formattedPrompt, null, null, {
                    add_special_tokens: false
                });
                console.log('[AI] Input IDs shape:', inputs.input_ids.dims);

                // Set up streaming with throttled partial-JSON parsing
                let streamer = null;
                if (onPartial && window.transformers && window.transformers.TextStreamer) {
                    let buffer = '';
                    let lastParseLength = 0;
                    const PARSE_INTERVAL_CHARS = 80; // re-parse every ~80 new chars
                    let lastEmitted = null;

                    const tokenizerForStreamer = this.processor.tokenizer || this.processor;
                    streamer = new window.transformers.TextStreamer(tokenizerForStreamer, {
                        skip_prompt: true,
                        skip_special_tokens: true,
                        callback_function: (text) => {
                            buffer += text;
                            if (buffer.length - lastParseLength < PARSE_INTERVAL_CHARS) return;
                            lastParseLength = buffer.length;

                            const partial = tryRepairTruncatedJson(buffer);
                            if (partial && JSON.stringify(partial) !== JSON.stringify(lastEmitted)) {
                                lastEmitted = partial;
                                try {
                                    onPartial(partial);
                                } catch (e) {
                                    console.warn('[AI] onPartial callback threw:', e);
                                }
                            }
                        }
                    });
                    console.log('[AI] Streaming enabled with partial-JSON parsing');
                }

                console.log('[AI] Step 4: Generating (this may take 30-60 seconds)...');
                const generateArgs = {
                    ...inputs,
                    max_new_tokens: maxTokens,
                    do_sample: false
                };
                if (streamer) generateArgs.streamer = streamer;

                const outputs = await this.model.generate(generateArgs);
                console.log('[AI] Generation complete!');

                console.log('[AI] Step 5: Decoding output...');
                const decoded = this.processor.batch_decode(
                    outputs.slice(null, [inputs.input_ids.dims.at(-1), null]),
                    { skip_special_tokens: true }
                );
                const generatedText = decoded[0];

                console.log('[AI] Generated text length:', generatedText.length);
                console.log('[AI] Generated text:', generatedText);
                const parsed = extractFirstJson(generatedText);
                console.log('[AI] Parsed JSON:', parsed);
                
                if (!parsed) {
                    console.warn('[AI] Failed to extract JSON from response');
                    console.log('[AI] First 500 chars:', generatedText.substring(0, 500));
                }
                
                return { raw: generatedText, parsed };
            } else {
                // Legacy pipeline API fallback
                console.log('[AI] Using legacy pipeline API');
                const result = await this.model(prompt, {
                    max_new_tokens: maxTokens,
                    do_sample: false
                });
                
                let generatedText = result[0].generated_text;
                if (generatedText.startsWith(prompt)) {
                    generatedText = generatedText.slice(prompt.length).trim();
                }
                
                console.log('[AI] Generated text:', generatedText);
                const parsed = extractFirstJson(generatedText);
                console.log('[AI] Parsed JSON:', parsed);
                
                return { raw: generatedText, parsed };
            }
        } catch (error) {
            console.error('[AI] Analysis error:', error);
            throw error;
        }
    }
}

// Global tracking to detect multiple instances across tabs
if (!window.__AI_SERVICE_INSTANCES__) {
    window.__AI_SERVICE_INSTANCES__ = [];
}

export const aiService = new AIService();
window.__AI_SERVICE_INSTANCES__.push(aiService._instanceId);

console.log(`[AI] Total AI service instances in this window:`, window.__AI_SERVICE_INSTANCES__.length);
console.log(`[AI] Instance IDs:`, window.__AI_SERVICE_INSTANCES__);

export default aiService;
